import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useRecoilState, useRecoilValue } from 'recoil';
import UrlParse from 'url-parse';
import * as faceapi from 'face-api.js';
import axios from 'axios';

import service from '../../service';
import Auth from '../../containers/Auth';
import End from '../../containers/End';
import Room from '../../containers/Room';
import * as cookiesManager from '../../utils/cookiesManager';

import { authenticationSelector } from '../../stores/authentication/selectors';
import { roomSelector } from '../../stores/room/selectors';

import RoomContext from '../../RoomContext';
import deviceInfo from '../../utils/deviceInfo';
import Logger from '../../Logger';
import RoomClient from '../../RoomClient';
import * as stateActions from '../../redux/stateActions';

import { getAPIUrl } from '../../utils/urlFactory';

const logger = new Logger();

const RoomInit = forwardRef(({ type, isProvider, faceDetection, useMicCamera }, ref) => {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.user);

    const [roomClient, setRoomClient] = useState(null);
    const authenticationStore = useRecoilValue(authenticationSelector);
    const [roomStore, setRoomStore] = useRecoilState(roomSelector);
    logger.debug('run() [environment:%s]', process.env.NODE_ENV);

    const loadModels = () => {
        Promise.all([
            faceapi.nets.tinyFaceDetector.loadFromUri('/resources/models'),
            faceapi.nets.faceLandmark68Net.loadFromUri('/resources/models'),
            faceapi.nets.faceRecognitionNet.loadFromUri('/resources/models'),
            faceapi.nets.faceExpressionNet.loadFromUri('/resources/models'),
        ])
            .then(() => {
                console.log('models loaded');
            })
            .catch((e) => console.log('loadModels error:', e));
    };

    useEffect(() => {
        if (!useMicCamera === undefined) return;

        const urlParser = new UrlParse(window.location.href, true);
        const peerId = authenticationStore.session;
        const roomId = roomStore.roomId;
        const displayName = authenticationStore.displayName;
        const handler = urlParser.query.handler;
        const useSimulcast = urlParser.query.simulcast !== 'false';
        const useSharingSimulcast = urlParser.query.sharingSimulcast !== 'false';
        const forceTcp = urlParser.query.forceTcp === 'true';
        const produce = useMicCamera;
        const consume = urlParser.query.consume !== 'false';
        const forceH264 = urlParser.query.forceH264 === 'true';
        const forceVP9 = urlParser.query.forceVP9 === 'true';
        const svc = urlParser.query.svc;
        const datachannel = urlParser.query.datachannel !== 'false';
        const info = urlParser.query.info === 'true';
        const externalVideo = urlParser.query.externalVideo === 'true';
        const throttleSecret = urlParser.query.throttleSecret;

        // Enable face detection on demand.
        if (faceDetection) {
            loadModels();
        }

        if (info) {
            // eslint-disable-next-line require-atomic-updates
            window.SHOW_INFO = true;
        }

        if (throttleSecret) {
            // eslint-disable-next-line require-atomic-updates
            window.NETWORK_THROTTLE_SECRET = throttleSecret;
        }

        // Get the effective/shareable Room URL.
        const roomUrlParser = new UrlParse(window.location.href, true);

        for (const key of Object.keys(roomUrlParser.query)) {
            // Don't keep some custom params.
            switch (key) {
                case 'roomId':
                case 'handler':
                case 'simulcast':
                case 'sharingSimulcast':
                case 'produce':
                case 'consume':
                case 'forceH264':
                case 'forceVP9':
                case 'forceTcp':
                case 'svc':
                case 'datachannel':
                case 'info':
                case 'faceDetection':
                case 'externalVideo':
                case 'throttleSecret':
                    break;
                default:
                    delete roomUrlParser.query[key];
            }
        }
        delete roomUrlParser.hash;

        const roomUrl = roomUrlParser.toString();

        // Get current device info.
        const device = deviceInfo();

        dispatch(stateActions.setRoomUrl(roomUrl));

        dispatch(stateActions.setRoomFaceDetection(faceDetection));

        dispatch(stateActions.setMe({ peerId, displayName, displayNameSet: false, device }));

        const _roomClient = new RoomClient({
            roomId,
            peerId,
            displayName,
            device,
            handlerName: handler,
            useSimulcast,
            useSharingSimulcast,
            forceTcp,
            produce,
            consume,
            forceH264,
            forceVP9,
            svc,
            datachannel,
            externalVideo,
            setRoomStore,
            role: authenticationStore.role,
            user,
        });
        // NOTE: For debugging.
        // eslint-disable-next-line require-atomic-updates
        window.CLIENT = _roomClient;
        // eslint-disable-next-line require-atomic-updates
        window.CC = _roomClient;

        // NOTE: Debugging stuff.
        window.__sendSdps = function () {
            logger.warn('>>> send transport local SDP offer:');
            logger.warn(_roomClient._sendTransport._handler._pc.localDescription.sdp);

            logger.warn('>>> send transport remote SDP answer:');
            logger.warn(_roomClient._sendTransport._handler._pc.remoteDescription.sdp);
        };

        window.__recvSdps = function () {
            logger.warn('>>> recv transport remote SDP offer:');
            logger.warn(_roomClient._recvTransport._handler._pc.remoteDescription.sdp);

            logger.warn('>>> recv transport local SDP answer:');
            logger.warn(_roomClient._recvTransport._handler._pc.localDescription.sdp);
        };

        let dataChannelTestInterval = null;

        window.__startDataChannelTest = function () {
            let number = 0;

            const buffer = new ArrayBuffer(32);
            const view = new DataView(buffer);

            dataChannelTestInterval = window.setInterval(() => {
                if (window.DP) {
                    view.setUint32(0, number++);
                    _roomClient.sendChatMessage(buffer);
                }
            }, 100);
        };

        window.__stopDataChannelTest = function () {
            window.clearInterval(dataChannelTestInterval);

            const buffer = new ArrayBuffer(32);
            const view = new DataView(buffer);

            if (window.DP) {
                view.setUint32(0, Math.pow(2, 32) - 1);
                window.DP.send(buffer);
            }
        };

        window.__testSctp = async function ({ timeout = 100, bot = false } = {}) {
            let dp;

            if (!bot) {
                await window.CLIENT.enableChatDataProducer();

                dp = window.CLIENT._chatDataProducer;
            } else {
                await window.CLIENT.enableBotDataProducer();

                dp = window.CLIENT._botDataProducer;
            }

            logger.warn(
                '<<< testSctp: DataProducer created [bot:%s, streamId:%d, readyState:%s]',
                bot ? 'true' : 'false',
                dp.sctpStreamParameters.streamId,
                dp.readyState
            );

            function send() {
                dp.send(`I am streamId ${dp.sctpStreamParameters.streamId}`);
            }

            if (dp.readyState === 'open') {
                send();
            } else {
                dp.on('open', () => {
                    logger.warn(
                        '<<< testSctp: DataChannel open [streamId:%d]',
                        dp.sctpStreamParameters.streamId
                    );

                    send();
                });
            }

            setTimeout(() => window.__testSctp({ timeout, bot }), timeout);
        };

        setInterval(() => {
            if (window.CLIENT._sendTransport) {
                window.H1 = window.CLIENT._sendTransport._handler;
                window.PC1 = window.CLIENT._sendTransport._handler._pc;
                window.DP = window.CLIENT._chatDataProducer;
            } else {
                delete window.PC1;
                delete window.DP;
            }

            if (window.CLIENT._recvTransport) {
                window.H2 = window.CLIENT._recvTransport._handler;
                window.PC2 = window.CLIENT._recvTransport._handler._pc;
            } else {
                delete window.PC2;
            }
        }, 2000);

        setRoomClient(_roomClient);
    }, []);

    const audioProducer = useSelector((state) =>
        Object.values(state.producers).find((producer) => producer.track.kind === 'audio')
    );
    const videoProducer = useSelector((state) =>
        Object.values(state.producers).find((producer) => producer.track.kind === 'video')
    );

    useImperativeHandle(
        ref,
        () => ({
            micControl() {
                if (!audioProducer) {
                    roomClient.enableMic();
                } else if (audioProducer?.paused === false) {
                    const devicesCookie = cookiesManager.getDevices();
                    cookiesManager.setDevices({ ...devicesCookie, micEnabled: false });

                    roomClient.muteMic(roomStore.roomId, authenticationStore.role);
                } else if (audioProducer?.paused === true) {
                    const devicesCookie = cookiesManager.getDevices();
                    cookiesManager.setDevices({ ...devicesCookie, micEnabled: true });

                    roomClient.unmuteMic();
                }
            },
            webcamControl() {
                if (videoProducer && videoProducer?.type !== 'share') {
                    const devicesCookie = cookiesManager.getDevices();
                    cookiesManager.setDevices({ ...devicesCookie, webcamEnabled: false });

                    roomClient.disableWebcam();
                } else {
                    const devicesCookie = cookiesManager.getDevices();
                    cookiesManager.setDevices({ ...devicesCookie, webcamEnabled: true });

                    roomClient.enableWebcam();
                }
            },
            trunClientMicOff() {
                if (!audioProducer) return;

                const devicesCookie = cookiesManager.getDevices();
                cookiesManager.setDevices({ ...devicesCookie, micEnabled: false });

                roomClient.muteMic(roomStore.roomId, authenticationStore.role);
            },
            turnClientWebcamOff() {
                const devicesCookie = cookiesManager.getDevices();
                cookiesManager.setDevices({ ...devicesCookie, webcamEnabled: false });

                roomClient.disableWebcam();
            },
            async clientLeaveRoom(callback) {
                console.log('callbackcallback',callback);
                // await roomClient.processUploadAudios(roomStore.roomId, authenticationStore.role);
                roomClient.close();
                setRoomStore({ leftRoom: true });

                if (callback) {
                    callback();
                }
            },
            providerCloseRoom(callback) {
                roomClient.muteMic(roomStore.roomId, authenticationStore.role);
                cookiesManager.setDevices({ webcamEnabled: false, micEnabled: false });
                roomClient.disableWebcam();

                roomClient.close();
                setRoomStore({ leftRoom: true });

                service
                    .endRoom(roomStore.roomId)
                    .then((res) => {
                        console.log('endRoom res', res);
                    })
                    .catch((err) => {
                        console.log('endRoom err', err);
                    })
                    .finally(async () => {
                        // await roomClient.processUploadAudios(
                        //     roomStore.roomId,
                        //     authenticationStore.role
                        // );

                        // await axios
                        //     .post(`${getAPIUrl()}/api/transcribe/process/audio-to-text-sync`, {
                        //         roomId: roomStore.roomId,
                        //         userName: 'GG',
                        //         formatType: 'Multiple',
                        //     })
                        //     .then((res) => {
                        //         console.log('audio-to-text-sync res', res);
                        //     })
                        //     .catch((err) => {
                        //         console.log('audio-to-text-sync err', err);
                        //     });

                        if (callback) {
                            callback();
                        }
                    });
            },
        }),
        [roomClient, authenticationStore, audioProducer, videoProducer]
    );

    if (roomClient) {
        return (
            <div>
                <RoomContext.Provider value={roomClient}>
                    <Room notCheckPermission={!useMicCamera} type={type} isProvider={isProvider} />
                </RoomContext.Provider>
            </div>
        );
    } else {
        return <div>no roomClient</div>;
    }
});

const Main = forwardRef(({ type, isProvider, faceDetection, useMicCamera }, ref) => {
    const [authenticationStore, setAuthenticationStore] = useRecoilState(authenticationSelector);
    const [roomStore, setRoomStore] = useRecoilState(roomSelector);
    const urlParser = new UrlParse(window.location.href, true);
    useEffect(() => {
        console.log('urlParser.query', urlParser.query);
        setAuthenticationStore({
            session: urlParser.query.session,
        });
        setRoomStore({
            roomId: urlParser.query.roomId,
        });
    }, []);

    useEffect(() => {
        const verify = async () => {
            if (authenticationStore.session && roomStore.roomId) {
                const verifyResult = await service.verifyRoomAccessing(roomStore.roomId);
                if (verifyResult.code === 200) {
                    if (verifyResult.data.bypass && verifyResult.data.authorized) {
                        const joinResult = await service.joinRoom(roomStore.roomId);
                        if (joinResult.code === 200) {
                            setAuthenticationStore({
                                bypass: verifyResult.data.bypass,
                                authorized: verifyResult.data.authorized,
                                role: joinResult.data.role,
                                displayName: joinResult.data.displayName,
                                session: joinResult.data.session,
                                loading: false,
                            });
                        } else {
                            setAuthenticationStore({
                                authorized: false,
                                loading: false,
                            });
                        }
                    } else {
                        setAuthenticationStore({
                            bypass: verifyResult.data.bypass,
                            authorized: verifyResult.data.authorized,
                            loading: false,
                        });
                    }
                } else {
                    setAuthenticationStore({
                        authorized: false,
                        loading: false,
                    });
                }
            }
        };

        if (roomStore.roomId) {
            verify();
        } else if (roomStore.roomId === undefined) {
            setAuthenticationStore({
                authorized: false,
                loading: false,
            });
        }
    }, [roomStore.roomId]);

    return (
        <div>
            {!authenticationStore.loading && (
                <>
                    {roomStore.leftRoom && <End />}
                    {!roomStore.leftRoom && (
                        <>
                            {(authenticationStore.bypass || authenticationStore.authorized) && (
                                <RoomInit
                                    ref={ref}
                                    type={type}
                                    isProvider={isProvider}
                                    faceDetection={faceDetection}
                                    useMicCamera={useMicCamera || false}
                                />
                            )}
                            {!authenticationStore.authorized && <Auth />}
                        </>
                    )}
                </>
            )}
        </div>
    );
});

export default Main;
